/**
 * The two Xero **Sales defaults**, asked at the moment a quote is pushed.
 *
 * A quote cannot reach Xero unless its **company** carries both
 * `xeroSalesLineAmountType` and `xeroSalesTaxType`; either missing is a 422
 * (`XERO_NO_LINE_AMOUNT_TYPE` / `XERO_NO_TAX_TYPE`). Collecting them at deal
 * entry was rejected: it puts a finance question in front of every deal that
 * never reaches Xero, asked of a salesperson rather than the person pushing.
 *
 * ⚠️ Nothing is ever pre-filled with a guess. The two failure modes aren't
 * symmetrical: a blank produces a loud refusal before anything leaves EMERGE,
 * while a guess produces a *successful* push carrying the wrong tax onto a
 * document the customer receives.
 *
 * ⚠️ It is opened from a quote but writes to a **company** — saving changes
 * the default for every future quote and invoice for that customer, not just
 * the one on screen. Said in the body copy, beside the controls, and on the
 * button, which names the record it writes to rather than saying "Save".
 *
 * The controls are `XeroSalesDefaultsFields`, shared with the company form,
 * so the two places these are set can never disagree about wording, which
 * rates are offered, or what a blank means.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { ApiError, api } from '@/lib/api';
import { documentCustomerName } from '@/lib/company-names';
import type { Company, CompanyInput } from '@/lib/types';
import { toLineAmountType, useXeroTaxRates } from '@/lib/xero-tax-rates';
import { useXero } from '@/lib/xero-context';
import { useAuth } from '@/lib/use-auth';
import { denialReason } from '@/lib/permissions';
import { Button, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { XeroSalesDefaultsFields, type XeroSalesDefaultsKey } from './XeroSalesDefaultsFields';

/** Just the company fields this dialog reads. A whole `Company` satisfies it. */
export type XeroSalesDefaultsCompany = Pick<
  Company,
  'id' | 'name' | 'billingName' | 'xeroSalesLineAmountType' | 'xeroSalesTaxType' | 'xeroSalesTaxName'
>;

export interface XeroSalesDefaultsDialogProps {
  open: boolean;
  /**
   * The company the QUOTE belongs to — the record this dialog writes to.
   * Pass the freshest copy you hold; the baseline for the diff comes from it.
   */
  company: XeroSalesDefaultsCompany;
  /**
   * Which defaults the push refused for, when the caller knows.
   *
   * Omit and it is derived from the company's own nulls, which is the same
   * answer in every ordinary case. Passing it lets the copy name exactly what
   * the server objected to rather than what merely looks absent.
   */
  missing?: readonly XeroSalesDefaultsKey[];
  /** Optional context line — e.g. the quote number this was opened from. */
  context?: ReactNode;
  onClose: () => void;
  /**
   * The saved company, straight from the PATCH response.
   *
   * The dialog does NOT close itself on success — the caller decides whether
   * to continue to the push, and closing first would make a failure there land
   * on a screen with no explanation of where it came from.
   */
  onSaved: (company: Company) => void;
}

type Values = Record<XeroSalesDefaultsKey, string>;

const KEYS: readonly XeroSalesDefaultsKey[] = ['xeroSalesLineAmountType', 'xeroSalesTaxType'];

const FIELD_NAMES: Record<XeroSalesDefaultsKey, string> = {
  xeroSalesLineAmountType: 'the line amount default',
  xeroSalesTaxType: 'the sales tax rate',
};

export function XeroSalesDefaultsDialog({
  open,
  company,
  missing,
  context,
  onClose,
  onSaved,
}: XeroSalesDefaultsDialogProps) {
  const { ability } = useAuth();
  const xero = useXero();

  /*
   * Nothing is requested while the connection is still being read or is
   * absent: there is no tenant to ask, and a request that could only fail
   * would put a red block on a dialog whose real state is "Xero is not
   * connected" — a different sentence with a different next step.
   */
  const taxRates = useXeroTaxRates(open && !xero.loading && xero.connection.connected);

  /*
   * Keyed on the two stored VALUES, never on the `company` object.
   *
   * A caller that builds the prop inline hands over a new object every render;
   * a baseline memoised on that identity would change every render, and the
   * reset effect below would wipe what the user had just chosen on every
   * keystroke elsewhere in the tree. Primitives cannot do that.
   */
  const storedLineAmountType = company.xeroSalesLineAmountType;
  const storedTaxType = company.xeroSalesTaxType;
  /* Named by its Billing name (the Xero Contact Name), owner 2026-10-25. */
  const customerName = documentCustomerName(company);
  const baseline = useMemo<Values>(
    () => ({
      xeroSalesLineAmountType: storedLineAmountType ?? '',
      xeroSalesTaxType: storedTaxType ?? '',
    }),
    [storedLineAmountType, storedTaxType],
  );
  const [values, setValues] = useState<Values>(baseline);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [notApplied, setNotApplied] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /*
   * Reopening must never show the previous attempt's errors, and must start
   * from what the company says NOW — the caller may have reloaded it since.
   *
   * Adjusted during render on the open transition rather than in an effect.
   * An effect would paint the stale values for a frame first, and keying it on
   * `baseline` as well would throw away what the user had chosen if the caller
   * refetched the company while the dialog was open.
   */
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValues(baseline);
      setServerErrors({});
      setFormError(null);
      setNotApplied(null);
      setSubmitting(false);
    }
  }

  /*
   * `api.updateCompany` takes no signal, so this controller cannot cancel the
   * request itself — what it does is mark the attempt dead the moment the
   * dialog closes or unmounts, so neither the catch nor the finally writes
   * state into a component that is no longer on screen.
   */
  const attempt = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    attempt.current = controller;
    return () => controller.abort();
  }, [open]);

  const canUpdate = ability.canUpdate('companies');
  const denied = canUpdate ? null : denialReason(ability, 'update', 'companies');

  const changed = useMemo(
    () => KEYS.filter((key) => values[key] !== baseline[key]),
    [values, baseline],
  );

  /*
   * Which defaults the user has moved BACK to "use organisation settings".
   *
   * Handed to the shared fields, which narrate it: `null` clears our value and
   * stops us sending a default, but it does not clear the override already on
   * the Xero contact — Xero has no way to express "unset".
   */
  const cleared = useMemo(
    () =>
      KEYS.filter((key) => baseline[key] !== '' && values[key] === '').map(
        (key) => FIELD_NAMES[key],
      ),
    [values, baseline],
  );

  /*
   * Left blank, the push is still refused — which is worth saying, because the
   * control legitimately offers "Use organisation settings" as a real answer
   * for invoicing in general and it is simply not one HERE.
   */
  const stillBlank = KEYS.filter((key) => values[key] === '');

  const refused = missing ?? KEYS.filter((key) => baseline[key] === '');

  async function save() {
    if (!canUpdate || changed.length === 0 || submitting) return;
    const signal = attempt.current?.signal;

    setSubmitting(true);
    setServerErrors({});
    setFormError(null);
    setNotApplied(null);

    /*
     * Only the keys that actually moved. An omitted key is untouched, an
     * explicit `null` clears, and an empty patch is a 422 — which is why the
     * button is disabled rather than this being allowed to happen.
     *
     * Written key by key rather than looped so each carries its own type.
     * `xeroSalesTaxName` is read-only and is deliberately absent from `Values`
     * entirely, so it cannot reach a payload by any route.
     */
    const payload: CompanyInput = {};
    if (changed.includes('xeroSalesLineAmountType')) {
      payload.xeroSalesLineAmountType = toLineAmountType(values.xeroSalesLineAmountType);
    }
    if (changed.includes('xeroSalesTaxType')) {
      payload.xeroSalesTaxType = values.xeroSalesTaxType === '' ? null : values.xeroSalesTaxType;
    }

    try {
      const result = await api.updateCompany(company.id, payload);
      if (signal?.aborted) return;

      /*
       * A 200 is not proof the value landed. Compared case-insensitively for
       * the tax code, because the server normalises it to Xero's own casing
       * (`output2` is stored `OUTPUT2`) — that is the write being applied, not
       * ignored. Anything genuinely dropped must stop the flow here: continuing
       * to the push would collect the same 422 with nothing explaining it.
       */
      const dropped: string[] = [];
      if (
        'xeroSalesLineAmountType' in payload &&
        (payload.xeroSalesLineAmountType ?? null) !== (result.data.xeroSalesLineAmountType ?? null)
      ) {
        dropped.push(FIELD_NAMES.xeroSalesLineAmountType);
      }
      if (
        'xeroSalesTaxType' in payload &&
        (payload.xeroSalesTaxType?.toUpperCase() ?? null) !==
          (result.data.xeroSalesTaxType?.toUpperCase() ?? null)
      ) {
        dropped.push(FIELD_NAMES.xeroSalesTaxType);
      }

      if (dropped.length > 0) {
        const verb = dropped.length === 1 ? 'was' : 'were';
        setNotApplied(
          `${customerName} was saved, but ${joinNames(dropped)} ${verb} not recorded. Set it again — the push is refused until it is stored.`,
        );
        setSubmitting(false);
        return;
      }

      onSaved(result.data);
    } catch (caught) {
      if (signal?.aborted) return;
      if (caught instanceof ApiError) {
        /*
         * A 403 body carries no `details` at all, unlike a 422 — so it is a
         * banner and never a field error. It can happen with the save button
         * visible: the role can change under an access token that lasts over
         * five days, so the gating above is an optimisation, not a guarantee.
         */
        if (caught.status === 403) {
          setFormError(
            `Your account is not permitted to edit ${customerName}. Ask someone who can.`,
          );
        } else {
          /*
           * ⚠️ This dialog writes to a COMPANY but renders only the two Sales
           * defaults, so most of that resource's keys have no control here.
           * Splitting against `KEYS` — the two it actually sends — is what
           * keeps a 422 about, say, `name` out of the count and into the
           * banner. Routing it by path would mark nothing while still claiming
           * a field is highlighted below, which is the silent failure this
           * split exists to prevent; an EMPTY path is not the only path that
           * binds to nothing.
           */
          const { fields, unroutable } = caught.routeFieldErrors(KEYS);
          setServerErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
    } finally {
      if (!signal?.aborted) setSubmitting(false);
    }
  }

  const errorCount = Object.keys(serverErrors).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Xero sales defaults"
      size="sm"
      dismissOnBackdrop={false}
      description={
        <>
          Xero will not accept this quote until{' '}
          <strong className="font-medium">{customerName}</strong> carries both of these.
          {context ? <> {context}</> : null}
        </>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={save}
            loading={submitting}
            disabled={!canUpdate || changed.length === 0}
            title={denied ?? undefined}
          >
            Save to company
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {/*
         * The load-bearing sentence. This dialog opens from a quote and writes
         * to a different record; someone who reads it as "settings for this
         * quote" will set a one-off rate and change every future document for
         * that customer without knowing.
         */}
        {/* `role="note"` is kept on a wrapper: `Notice` takes only `alert` / `status`. */}
        <div role="note">
          <Notice
            tone="warning"
            className="break-words"
            title="This saves to the company, not to this quote."
          >
            It becomes the default for{' '}
            <strong className="font-semibold">every future quote and invoice</strong> for{' '}
            {customerName}.
          </Notice>
        </div>

        {refused.length > 0 && (
          <p className="text-xs text-fg-muted">
            Xero refused the push because {joinNames(refused.map((key) => FIELD_NAMES[key]))} is not
            set on this customer.
          </p>
        )}

        {notApplied && (
          <Notice tone="warning" role="alert" className="break-words">
            {notApplied}
          </Notice>
        )}

        {/*
          The shared summary rather than a second copy of its wording: the rule
          that only routable errors may be counted lives in one place, and a
          form message and a count now render together instead of the message
          silently swallowing the count.
        */}
        <FormErrorSummary formError={formError} fieldErrorCount={errorCount} />

        <XeroSalesDefaultsFields
          layout="stacked"
          lineAmountType={values.xeroSalesLineAmountType}
          taxType={values.xeroSalesTaxType}
          errors={serverErrors}
          rates={taxRates.rates}
          ratesLoading={taxRates.loading}
          ratesError={taxRates.error}
          onReloadRates={taxRates.reload}
          connectionLoading={xero.loading}
          connected={xero.connection.connected}
          notInstalled={xero.notInstalled}
          storedTaxType={company.xeroSalesTaxType}
          storedTaxName={company.xeroSalesTaxName}
          cleared={cleared}
          onChange={(key, next) => {
            setValues((prev) => ({ ...prev, [key]: next }));
            // An error about a value the user has since changed is noise.
            setServerErrors((prev) => {
              if (!(key in prev)) return prev;
              const rest = { ...prev };
              delete rest[key];
              return rest;
            });
          }}
        />

        {stillBlank.length > 0 && (
          <p className="text-xs text-fg-muted">
            Left on &ldquo;Use organisation settings&rdquo;,{' '}
            {joinNames(stillBlank.map((key) => FIELD_NAMES[key]))} is still unset and the quote will
            be refused again.
          </p>
        )}

        {denied && (
          <p className="text-xs text-fg-muted">
            {denied} Ask someone who can edit {customerName} to set these on the company record.
          </p>
        )}
      </div>
    </Modal>
  );
}

function joinNames(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
